import Check from '@mui/icons-material/Check'
import { alpha, Backdrop, Box, keyframes, LinearProgress, Stack, Typography } from '@mui/material'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Decorative "checklist building up" overlay shown while a past order is loaded
// into the basket (reorder). Adapted from the design mockup on PT-1059 —
// staggered rows slide in, their bars fill, then an indicator ticks "done" —
// but rebuilt from MUI primitives + an Emotion keyframe instead of the mockup's
// hand-rolled CSS/setTimeout JS, and driven entirely by tenant theme tokens
// rather than the mockup's example hex.
//
// The animation is indeterminate: it loops while `open` is true and the overlay
// simply unmounts when loading finishes. The row count and bar widths are
// cosmetic — they are NOT tied to the real items being loaded.

// Faux rows. Each has two bar target widths (%), mirroring the mockup's
// two-line item shape. Kept at 4 rows — a 5th was clipped at the bottom of
// the overlay in the basket body.
const ROWS: { bars: [number, number] }[] = [
  { bars: [72, 45] },
  { bars: [55, 80] },
  { bars: [88, 60] },
  { bars: [65, 40] },
]

const ROW_STAGGER_MS = 220 // gap between each row entering
const ROW_FILL_MS = 450 // bar fill / done transition per row
const HOLD_MS = 600 // pause on the completed checklist before looping

// Emotion keyframes — the mockup's `@keyframes blink` for the trailing dots and
// a scale-in "bounce" for the tick, expressed via MUI's re-exported keyframes.
const blink = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
`

const tickIn = keyframes`
  from { transform: scale(0); }
  to { transform: scale(1); }
`

const LoadingDots: React.FC = () => (
  <Box component="span" aria-hidden sx={{ display: 'inline-flex' }}>
    {[0, 0.2, 0.4].map((delay) => (
      <Box
        key={delay}
        component="span"
        sx={{
          // Reduced-motion: keep the dots steady rather than blinking.
          '@media (prefers-reduced-motion: no-preference)': {
            animation: `${blink} 1.2s infinite`,
            animationDelay: `${delay}s`,
          },
          '@media (prefers-reduced-motion: reduce)': { opacity: 1 },
          opacity: 0,
        }}>
        .
      </Box>
    ))}
  </Box>
)

interface ChecklistRowProps {
  bars: [number, number]
  /** Row is revealed (slides + fades in). */
  shown: boolean
  /** Row's bars are filled and its indicator is ticked. */
  done: boolean
}

const ChecklistRow: React.FC<ChecklistRowProps> = ({ bars, shown, done }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      // Entrance: slide in from the left + fade, like the mockup's rows. Driven
      // by a plain `sx` transition rather than nesting two MUI <Transition>s
      // (which fight over the same node's transform/opacity).
      opacity: shown ? 1 : 0,
      transform: shown ? 'translateX(0)' : 'translateX(-12px)',
      '@media (prefers-reduced-motion: no-preference)': {
        transition: 'opacity 0.35s ease, transform 0.35s ease',
      },
    }}>
    {/* Indicator circle — fills to primary + scales in a tick on done. */}
    <Box
      sx={{
        width: 16,
        height: 16,
        flexShrink: 0,
        borderRadius: '50%',
        border: '1.5px solid',
        borderColor: (theme) => (done ? 'primary.main' : alpha(theme.palette.primary.main, 0.35)),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 0.2s ease',
      }}>
      {done ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@media (prefers-reduced-motion: no-preference)': {
              animation: `${tickIn} 0.25s cubic-bezier(0.34,1.56,0.64,1)`,
            },
          }}>
          <Check sx={{ fontSize: 11, color: 'primary.contrastText' }} />
        </Box>
      ) : null}
    </Box>

    {/* Two bars — light fill in progress, full primary on done. */}
    <Stack spacing={0.625} sx={{ flex: 1 }}>
      {bars.map((target) => (
        <LinearProgress
          key={`bar-${target}`}
          variant="determinate"
          value={shown ? target : 0}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              // Only animate the fill when motion is allowed; under reduced
              // motion the bar snaps to its value, consistent with the dots,
              // tick and row entrance above.
              '@media (prefers-reduced-motion: no-preference)': {
                transition: `transform ${ROW_FILL_MS}ms cubic-bezier(0.4,0,0.2,1)`,
              },
              bgcolor: (theme) => (done ? 'primary.main' : alpha(theme.palette.primary.main, 0.4)),
            },
          }}
        />
      ))}
    </Stack>
  </Stack>
)

interface BasketLoadingOverlayProps {
  open: boolean
}

const BasketLoadingOverlay: React.FC<BasketLoadingOverlayProps> = ({ open }) => {
  const { t } = useTranslation()

  // `step` advances the sequence: each row is shown then marked done. The full
  // cycle has ROWS.length * 2 steps (show + done), then a hold, then it loops.
  const [step, setStep] = useState(0)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  useEffect(() => {
    if (!open) {
      setStep(0)
      return
    }

    const clear = () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
    }

    const runCycle = () => {
      clear()
      setStep(0)
      // Show row i, then mark it done a beat later, staggered down the list.
      ROWS.forEach((_, i) => {
        timers.current.push(setTimeout(() => setStep(i * 2 + 1), i * ROW_STAGGER_MS))
        timers.current.push(setTimeout(() => setStep(i * 2 + 2), i * ROW_STAGGER_MS + ROW_FILL_MS))
      })
      // Restart only after the last row has fully finished animating, plus a
      // hold. The last row's `done` step fires at lastRowDone; its bar-fill /
      // colour transition then takes a further ROW_FILL_MS to settle visually.
      // Waiting for that settle (not just the step) stops the first row from
      // appearing to restart while later rows are still animating.
      const lastRowDone = (ROWS.length - 1) * ROW_STAGGER_MS + ROW_FILL_MS
      const total = lastRowDone + ROW_FILL_MS + HOLD_MS
      timers.current.push(setTimeout(runCycle, total))
    }

    runCycle()
    return clear
  }, [open])

  return (
    <Backdrop
      open={open}
      sx={{
        position: 'absolute',
        // Anchor top/left/right but let the height follow our own content
        // (bottom:auto) instead of pinning to the parent body. The body
        // shrink-wraps us (index.tsx minHeight:auto while loading), so the
        // basket sizes to the animation rather than to whatever order was in
        // the cart before Load was clicked — no more dead padding on big orders.
        top: 0,
        left: 0,
        right: 0,
        bottom: 'auto',
        zIndex: (theme) => theme.zIndex.modal - 1,
        // Solid paper fill so the overlay reads as the basket body loading,
        // rather than a floating card on top of the empty basket behind it.
        bgcolor: 'background.paper',
        borderRadius: '12px',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px 20px',
        }}>
        <Typography
          role="status"
          aria-live="polite"
          align="center"
          sx={{
            fontSize: 13,
            letterSpacing: '0.04em',
            color: 'text.secondary',
            mb: 2.5,
          }}>
          {t('marketplace:addingYourItems')}
          <LoadingDots />
        </Typography>

        <Stack spacing={1.75}>
          {ROWS.map((row, i) => (
            <ChecklistRow
              key={`row-${row.bars[0]}-${row.bars[1]}`}
              bars={row.bars}
              shown={step >= i * 2 + 1}
              done={step >= i * 2 + 2}
            />
          ))}
        </Stack>
      </Box>
    </Backdrop>
  )
}

export default BasketLoadingOverlay
